import React, { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { Quote } from 'lucide-react';
import { functions } from '@/firebase';
import { Skeleton } from '@/shared/components/Skeleton';

/**
 * Approved testimonials, surfaced on the homepage only (Jordan's call: skip a
 * dedicated page for now, social proof belongs near the funnel top). Reads the
 * public, rate-limited `getApprovedTestimonials` callable, built for #296 but
 * never called anywhere until now.
 *
 * v1 is TEXT ONLY. `submitTestimonial` used to be video-only (`story` was
 * always written as ''), so nothing here could ever have rendered. The
 * backend now also accepts a written story; this section filters to entries
 * that have one, so an approved video-only testimonial (still no story) never
 * renders an empty card. Video embeds are a deliberate follow-up, not
 * half-built here.
 */

interface ApprovedTestimonial {
  id: string;
  displayName: string;
  story: string;
  examType: string;
  socialHandle?: string;
}

type LoadState = 'loading' | 'loaded' | 'error';

const MAX_SHOWN = 4;

export const TestimonialsSection: React.FC = () => {
  const [state, setState] = useState<LoadState>('loading');
  const [testimonials, setTestimonials] = useState<ApprovedTestimonial[]>([]);
  // Same "don't flash a loader on a fast response" tactic as BrandedLoader:
  // this is a supporting trust signal, not blocking content, so a fast
  // response should just appear instead of flickering a skeleton first.
  const [showSkeleton, setShowSkeleton] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const skeletonTimer = setTimeout(() => {
      if (!cancelled) setShowSkeleton(true);
    }, 300);

    (async () => {
      try {
        const getApprovedTestimonials = httpsCallable<
          void,
          { testimonials: ApprovedTestimonial[] }
        >(functions, 'getApprovedTestimonials');
        const result = await getApprovedTestimonials();
        if (cancelled) return;
        // Guard: the callable already filters to status === 'approved' server
        // side, so nothing unapproved ever reaches this component to begin
        // with. This filter is the TEXT-only v1 scope, not an approval check.
        const textOnly = (result.data.testimonials || []).filter(
          (t) => typeof t.story === 'string' && t.story.trim().length > 0
        );
        setTestimonials(textOnly);
        setState('loaded');
      } catch {
        if (!cancelled) setState('error');
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(skeletonTimer);
    };
  }, []);

  // Empty state: render nothing, both while there's genuinely nothing to show
  // and if the call fails, rather than surface an error in a trust section.
  if (state === 'error') return null;
  if (state === 'loaded' && testimonials.length === 0) return null;

  if (state === 'loading') {
    if (!showSkeleton) return null;
    return (
      <div className="mt-6" data-testid="testimonials-skeleton">
        <div className="grid gap-4 sm:grid-cols-2">
          {[0, 1].map((i) => (
            <div key={i} className="card">
              <Skeleton lines={3} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6" data-testid="testimonials-section">
      <p className="eyebrow text-center mb-3">What candidates are saying</p>
      <div className="grid gap-4 sm:grid-cols-2">
        {testimonials.slice(0, MAX_SHOWN).map((t) => (
          <figure key={t.id} className="card" data-testid="testimonial-card">
            <Quote className="w-5 h-5 text-primary-300 mb-2" aria-hidden="true" />
            <blockquote className="text-sm text-neutral-700 leading-relaxed">
              &quot;{t.story}&quot;
            </blockquote>
            <figcaption className="mt-3 text-xs font-bold text-neutral-500 uppercase tracking-wide">
              {t.displayName} · {t.examType}
              {t.socialHandle ? ` · ${t.socialHandle}` : ''}
            </figcaption>
          </figure>
        ))}
      </div>
      <p className="mt-3 text-xs text-neutral-500 text-center">
        Members who shared these stories received a free year of access.
      </p>
    </div>
  );
};

export default TestimonialsSection;
